import { useEffect, useRef, useState } from "react";
import type { AlignInTableConfig, TableCellAnchor } from "../../../types";
import styles from "./InlinePanel.module.css";

interface AlignInTablePanelProps {
  readonly busy: boolean;
  /** Fired on open and whenever a control changes — applies the alignment live. */
  readonly onChange: (cfg: AlignInTableConfig) => void;
  readonly onOk: () => void;
  readonly onCancel: () => void;
}

// 3×3 anchor grid in reading order (row-major), matching the VSTO dialog.
const ANCHORS: ReadonlyArray<{ key: TableCellAnchor; label: string }> = [
  { key: "TL", label: "Top left" },
  { key: "TC", label: "Top center" },
  { key: "TR", label: "Top right" },
  { key: "ML", label: "Middle left" },
  { key: "MC", label: "Middle center" },
  { key: "MR", label: "Middle right" },
  { key: "BL", label: "Bottom left" },
  { key: "BC", label: "Bottom center" },
  { key: "BR", label: "Bottom right" },
];

const DEFAULT_SIZE = 12.2;

/**
 * Inline panel for "Align Shapes as Table" (ported from the VSTO "Align shapes
 * in table" dialog): pick the shape size, a horizontal/vertical cell margin, and
 * one of nine anchor positions. The alignment applies live on open and on every
 * change (so switching position updates the slide in real time); OK keeps the
 * result and Cancel reverts it. Controls stay enabled during a live apply — the
 * parent serialises applies so rapid switches can't collide.
 */
export function AlignInTablePanel({ busy, onChange, onOk, onCancel }: AlignInTablePanelProps): React.ReactElement {
  const [size, setSize] = useState(DEFAULT_SIZE);
  const [horizontalSpace, setHorizontalSpace] = useState(0);
  const [verticalSpace, setVerticalSpace] = useState(0);
  const [anchor, setAnchor] = useState<TableCellAnchor>("MC");

  // Apply once on open (ref keeps the effect from re-firing on prop identity).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    onChangeRef.current({ size: DEFAULT_SIZE, horizontalSpace: 0, verticalSpace: 0, anchor: "MC" });
  }, []);

  // Re-apply with the current values plus the one field that just changed.
  const fire = (next: Partial<AlignInTableConfig>): void =>
    onChange({ size, horizontalSpace, verticalSpace, anchor, ...next });

  return (
    <fieldset className={styles.panel} aria-label="Align Shapes as Table options">
      <div className={styles.header}>
        <span className={styles.title}>Align Shapes as Table</span>
        <button type="button" className={styles.close} aria-label="Close" onClick={onCancel}>
          &times;
        </button>
      </div>

      <div className={styles.subLabel}>Margin</div>
      <div className={styles.rowInline}>
        <div className={styles.row}>
          <label htmlFor="ait-h">Horizontal</label>
          <input
            id="ait-h"
            type="number"
            step={0.1}
            value={horizontalSpace}
            aria-label="Horizontal margin"
            onChange={(e) => {
              const n = Number.parseFloat(e.target.value);
              if (!Number.isNaN(n)) {
                const v = Math.max(0, n);
                setHorizontalSpace(v);
                fire({ horizontalSpace: v });
              }
            }}
          />
        </div>
        <div className={styles.row}>
          <label htmlFor="ait-v">Vertical</label>
          <input
            id="ait-v"
            type="number"
            step={0.1}
            value={verticalSpace}
            aria-label="Vertical margin"
            onChange={(e) => {
              const n = Number.parseFloat(e.target.value);
              if (!Number.isNaN(n)) {
                const v = Math.max(0, n);
                setVerticalSpace(v);
                fire({ verticalSpace: v });
              }
            }}
          />
        </div>
      </div>

      <div className={styles.subLabel}>Position</div>
      <div className={styles.posGrid} role="radiogroup" aria-label="Cell position">
        {ANCHORS.map((a) => (
          <button
            key={a.key}
            type="button"
            className={`${styles.posCell} ${anchor === a.key ? styles.posCellActive : ""}`}
            role="radio"
            aria-checked={anchor === a.key}
            aria-label={a.label}
            title={a.label}
            onClick={() => {
              setAnchor(a.key);
              fire({ anchor: a.key });
            }}
          />
        ))}
      </div>

      <div className={styles.subLabel}>Size</div>
      <div className={styles.row}>
        <label htmlFor="ait-size">Shape size (pt)</label>
        <input
          id="ait-size"
          type="number"
          min={1}
          step={0.1}
          value={size}
          aria-label="Shape size"
          onChange={(e) => {
            const n = Number.parseFloat(e.target.value);
            if (!Number.isNaN(n)) {
              const v = Math.max(1, n);
              setSize(v);
              fire({ size: v });
            }
          }}
        />
      </div>

      <div className={styles.footer}>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className={styles.primary} onClick={onOk}>
          {busy ? "Working…" : "OK"}
        </button>
      </div>
    </fieldset>
  );
}
